import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceService, { CourseLicense, extractErrorMessage } from '../../../services/marketplaceService';
import { seatUsage } from '../../../pages/marketplaceVisibility';
import MaterialSymbol from '../../common/MaterialSymbol';
import { VisibilityBadge } from '../Seller/SellerBadges';
import marketplaceReviewService, { MarketplaceReview } from '../../../services/marketplaceReviewService';
import StarRating from '../Reviews/StarRating';
import ReviewDialog from '../Reviews/ReviewDialog';
import '../marketplace-design.css';

const formatDate = (value: string | null | undefined): string =>
    value ? new Date(value).toLocaleDateString('de-DE') : '';

/** Seat-Balken: belegte Plätze einer Teilnehmer-Lizenz (graue Fläche, Sekundärgrün, kein Verlauf). */
export const SeatBar: React.FC<{ license: Pick<CourseLicense, 'currentEnrollments' | 'maxEnrollments'> }> = ({ license }) => {
    const translate = useTranslate();
    const usage = seatUsage(license);

    return (
        <div className="mk-seats" data-testid="seat-bar">
            <div className="h">
                <span>{translate('marketplaceBuyer.licenses.seats')}</span>
                <b>
                    {usage.max === null
                        ? translate('marketplaceBuyer.licenses.seatsUnlimited', { used: usage.used })
                        : translate('marketplaceBuyer.licenses.seatsOf', { used: usage.used, max: usage.max })}
                </b>
            </div>
            {usage.max !== null && (
                <div
                    className="bar"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={usage.max}
                    aria-valuenow={usage.used}
                    aria-label={translate('marketplaceBuyer.licenses.seats')}
                >
                    <i className={usage.full ? 'full' : undefined} style={{ width: `${usage.percent}%` }} />
                </div>
            )}
            {usage.full && <span className="cap">{translate('marketplaceBuyer.licenses.seatsFull')}</span>}
        </div>
    );
};

/** Medienstatus einer Kopie-Lizenz (T3): läuft, fertig oder fehlgeschlagen mit „Erneut versuchen“. */
export const MediaStatus: React.FC<{ license: CourseLicense; onRetry: (license: CourseLicense) => void; retrying: boolean }> = ({
    license,
    onRetry,
    retrying,
}) => {
    const translate = useTranslate();
    const status = license.mediaMigrationStatus;
    if (!status) return null;

    const icon = status === 'done' ? 'check_circle' : status === 'failed' ? 'error' : 'hourglass_top';

    return (
        <div className={`mk-media${status === 'failed' ? ' failed' : ''}`} data-testid={`media-status-${status}`}>
            <MaterialSymbol name={icon} />
            <div className="tx">
                <b>{translate(`marketplaceBuyer.licenses.media.${status}.title`)}</b>
                <span>{translate(`marketplaceBuyer.licenses.media.${status}.hint`)}</span>
                {status === 'failed' && license.mediaMigrationError && <span className="e">{license.mediaMigrationError}</span>}
            </div>
            {status === 'failed' && (
                <button type="button" className="mk-btn-o mk-sm" disabled={retrying} onClick={() => onRetry(license)}>
                    <MaterialSymbol name="sync" />
                    {translate('marketplaceBuyer.licenses.media.retry')}
                </button>
            )}
        </div>
    );
};

/** Bewertung auf der Lizenz-Karte (T9): „Bewerten“ bei aktiver Lizenz, sonst die eigene Wertung mit „Ändern“. */
const LicenseReview: React.FC<{ license: CourseLicense; review: MarketplaceReview | null; onOpen: () => void }> = ({ license, review, onOpen }) => {
    const translate = useTranslate();
    if (!review && license.status !== 'active') return null;

    return (
        <div className="acts" data-testid={`license-review-${license.id}`}>
            {review ? (
                <span className="mk-yours">
                    <StarRating value={review.rating} size={18} />
                    <span>{translate('marketplaceReviews.yourRating', { count: review.rating })}</span>
                </span>
            ) : null}
            {license.status === 'active' && (
                <button type="button" className="mk-btn-o mk-sm" onClick={onOpen}>
                    <MaterialSymbol name="star" />
                    {translate(review ? 'marketplaceReviews.edit' : 'marketplaceReviews.write')}
                </button>
            )}
        </div>
    );
};

/** Lizenzstatus → Status-Pill der Vorlage (ok / warn / err / neu) mit Icon. */
const LICENSE_STATUS_PILL: Record<string, { tone: string; icon: string }> = {
    active: { tone: 'ok', icon: 'check_circle' },
    pending: { tone: 'warn', icon: 'hourglass_top' },
    suspended: { tone: 'warn', icon: 'pause' },
    expired: { tone: 'neu', icon: 'schedule' },
    revoked: { tone: 'err', icon: 'cancel' },
};

const LicenseCard: React.FC<{
    license: CourseLicense;
    retrying: boolean;
    onRetry: (l: CourseLicense) => void;
    review?: MarketplaceReview | null;
    onReview?: (l: CourseLicense) => void;
}> = ({
    license,
    retrying,
    onRetry,
    review = null,
    onReview,
}) => {
    const translate = useTranslate();
    const isCopy = license.licenseType !== 'reference';
    const showSeats = license.licenseType === 'seat_copy' || (license.maxEnrollments ?? 0) > 0;
    const pill = LICENSE_STATUS_PILL[license.status] ?? { tone: 'neu', icon: 'info' };
    const muted = license.status === 'revoked' || license.status === 'expired';

    return (
        <article className={`mk-lcard${muted ? ' muted' : ''}`} data-testid={`license-card-${license.id}`}>
            <div className="mk-row">
                {license.origin && license.origin !== 'own' && <VisibilityBadge visibility={license.origin} short />}
                <span className="mk-pill neu">{translate(`marketplaceBuyer.licenseType.${license.licenseType}.chip`, { _: license.licenseType })}</span>
                {license.listing?.isBundle && (
                    <span className="mk-pill neu" data-testid={`license-bundle-${license.id}`} title={license.listing.title}>
                        <MaterialSymbol name="layers" />
                        {translate('marketplaceBuyer.bundle.licensePill', { title: license.listing.title })}
                    </span>
                )}
                <span className={`mk-pill ${pill.tone}`}>
                    <MaterialSymbol name={pill.icon} />
                    {translate(`marketplaceBuyer.licenses.status.${license.status}`, { _: license.status })}
                </span>
            </div>
            <div>
                <h3>{license.sourceCourse.title}</h3>
                {license.licensedCourse && (
                    <div className="meta" style={{ marginTop: 4 }}>
                        <span>{translate('marketplaceBuyer.licenses.copyOf', { title: license.licensedCourse.title })}</span>
                    </div>
                )}
            </div>
            <div className="meta">
                <span>
                    <MaterialSymbol name="event" />
                    {translate('marketplaceBuyer.licenses.since', { date: formatDate(license.createdAt) })}
                    {license.expiresAt ? ` · ${translate('marketplaceBuyer.licenses.expires', { date: formatDate(license.expiresAt) })}` : ''}
                </span>
            </div>
            {showSeats && <SeatBar license={license} />}
            {isCopy && <MediaStatus license={license} retrying={retrying} onRetry={onRetry} />}
            {onReview && <LicenseReview license={license} review={review} onOpen={() => onReview(license)} />}
            {license.status === 'revoked' && license.revokeReason && (
                <div className="notice">
                    <MaterialSymbol name="info" />
                    {translate('marketplaceBuyer.licenses.revoked', { reason: license.revokeReason })}
                </div>
            )}
        </article>
    );
};

/**
 * „Meine Lizenzen“ (Plan T6): Karten mit Herkunft, Lizenzart, Seat-Balken bei
 * Teilnehmer-Lizenzen, Medienstatus bei Kopien und „Erneut versuchen“, wenn die
 * Medienmigration fehlgeschlagen ist. Die Kursverwaltung bleibt unter
 * „Lizenzierte Kurse“ (`/licensed-courses`).
 */
const BuyerLicensesPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [licenses, setLicenses] = useState<CourseLicense[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [retryingId, setRetryingId] = useState<number | null>(null);
    const [reviews, setReviews] = useState<Record<number, MarketplaceReview>>({});
    const [reviewing, setReviewing] = useState<CourseLicense | null>(null);

    const load = useCallback(async () => {
        setError(null);
        // Eigene Bewertungen sind nachrangig: ein Fehler lässt nur die Sterne weg
        marketplaceReviewService.getMyReviews().then(setReviews).catch(() => undefined);
        try {
            setLicenses(await marketplaceService.getMyLicenses());
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceBuyer.licenses.loadError')));
        } finally {
            setLoading(false);
        }
    }, [translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const retry = async (license: CourseLicense) => {
        setRetryingId(license.id);
        try {
            await marketplaceService.retryLicenseMedia(license.id);
            notify(translate('marketplaceBuyer.licenses.media.retryStarted'), { type: 'success' });
            await load();
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceBuyer.licenses.media.retryError')), { type: 'error' });
        } finally {
            setRetryingId(null);
        }
    };

    if (loading) return <div className="mk-skel" aria-busy="true" />;

    return (
        <div>
            {error && (
                <div className="mk-alert err" role="alert">
                    <MaterialSymbol name="error" />
                    <div className="body">{error}</div>
                </div>
            )}
            <div className="mk-sec-t">
                <div>
                    <h2>{translate('marketplaceBuyer.licenses.title')}</h2>
                    <p>{translate('marketplaceBuyer.licenses.intro')}</p>
                </div>
                <RouterLink to="/licensed-courses" className="mk-btn-o mk-sm">
                    <MaterialSymbol name="card_membership" />
                    {translate('marketplaceBuyer.licenses.manage')}
                </RouterLink>
            </div>
            {licenses.length === 0 && !error ? (
                <div className="mk-empty" role="status">
                    <MaterialSymbol name="card_membership" />
                    <p>{translate('marketplaceBuyer.licenses.empty')}</p>
                </div>
            ) : (
                <div className="mk-lgrid">
                    {licenses.map((license) => (
                        <LicenseCard
                            key={license.id}
                            license={license}
                            retrying={retryingId === license.id}
                            onRetry={retry}
                            review={reviews[license.id] ?? null}
                            onReview={setReviewing}
                        />
                    ))}
                </div>
            )}
            {reviewing && (
                <ReviewDialog
                    open
                    licenseId={reviewing.id}
                    courseTitle={reviewing.sourceCourse.title}
                    existing={reviews[reviewing.id] ?? null}
                    onClose={() => setReviewing(null)}
                    onSaved={(saved) => {
                        setReviews((current) => ({ ...current, [reviewing.id]: saved }));
                        setReviewing(null);
                    }}
                />
            )}
        </div>
    );
};

export default BuyerLicensesPanel;
